import React from 'react';
import * as R from 'ramda';
import { reduxForm } from 'redux-form';
import { Trans, withTranslation } from 'react-i18next';

import * as AlertService from '@common/services/alert';
import { Row, Group, TextInput } from '@common/components/form';
import Icon from '@common/components/icon';
import { Select } from '@common/components/form/inputs/select';
import AsyncList from '@common/components/list/async';
import { createRole, updateRole } from '../../actions';
import { Checkbox } from '@common/components/form/inputs/checkbox';

type Props = {
  level: number;
  role: any;
  submitting: boolean;
  invalid: boolean;
  onFetchGroups: (level: number) => any;
  onExited?: () => void;
  handleSubmit: (values: Object) => () => Promise<void>;
  initialize: (values: Object) => void;
  t: (key: string, values?: Object) => string;
  i18n: any;
  onDisabledChange?: (disabled: boolean) => void;
};

type State = {
  selectedGroups: Record<string, any>;
};

class RoleFormContent extends React.Component<Props, State> {
  static props: Props;

  constructor(props: Props) {
    super(props);

    this.state = {
      selectedGroups: {},
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleInitialiseForm = this.handleInitialiseForm.bind(this);
  }

  handleInitialiseForm({ data: groups }: any) {
    const { role } = this.props;

    if (!role) return;

    const permissionInRole = (permission: any) => {
      return role.permissions.includes(permission);
    };

    this.setState({
      selectedGroups: groups.reduce((acc: any, group: any) => {
        if (group.options) {
          const selectedSubLevel = group.options.filter((subLevel: any) => {
            return subLevel.permissions.every(permissionInRole);
          })[0];

          if (!selectedSubLevel) return acc;

          return R.assoc(group.id, selectedSubLevel.permissions, acc);
        }

        if (group.permissions.every(permissionInRole)) return R.assoc(group.id, group.permissions, acc);

        return acc;
      }, {}),
    });

    this.props.initialize({ name: role.name });
  }

  handleChange(item: { id: string, permissions: Array<string> }, value: boolean) {
    const { selectedGroups } = this.state;
    const newSelectedGroups = value
      ? R.assoc(item.id, item.permissions, selectedGroups)
      : R.omit([item.id], selectedGroups);

    this.setState({ selectedGroups: newSelectedGroups });

    // Notify parent of disabled state change
    if (this.props.onDisabledChange) {
      const { invalid } = this.props;
      const disabled = invalid || Object.keys(newSelectedGroups).length === 0;
      this.props.onDisabledChange(disabled || (this.props.role && this.props.role.is_global));
    }
  }

  async handleSubmit(values: any, dispatch: any) {
    const { role, level, t, onExited } = this.props;

    try {
      if (role) {
        await dispatch(updateRole(role.id, { name: values.name, permissions: R.flatten(R.values(this.state.selectedGroups)) }));
      } else {
        await dispatch(createRole({ name: values.name, permissions: R.flatten(R.values(this.state.selectedGroups)), level }));
      }

      if (onExited) onExited();

      AlertService.success(
        role ?
          t('organisation:forms_role_edited', { name: values.name }) :
          t('organisation:forms_role_created')
      );
    } catch (response: any) {
      AlertService.forStatus(response.status_code, {
        warning: role ?
          t('organisation:forms_role_edit_warning') :
          t('organisation:forms_role_create_warning'),
        error: role ?
          t('organisation:forms_role_edit_warning') :
          t('organisation:forms_role_create_error')
      });
    }
  }

  componentDidMount() {
    // Initialize disabled state
    if (this.props.onDisabledChange) {
      const { invalid, role } = this.props;
      const { selectedGroups } = this.state;
      const disabled = invalid || Object.keys(selectedGroups).length === 0;
      this.props.onDisabledChange(disabled || (role && role.is_global));
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { selectedGroups } = this.state;
    const { invalid, role, onDisabledChange } = this.props;

    if (onDisabledChange && (
      prevState.selectedGroups !== selectedGroups ||
      prevProps.invalid !== invalid
    )) {
      const disabled = invalid || Object.keys(selectedGroups).length === 0;
      onDisabledChange(disabled || (role && role.is_global));
    }
  }

  render() {
    const { selectedGroups } = this.state;
    const {
      role, level, onFetchGroups,
      handleSubmit, t, i18n
    } = this.props;

    return (
      <form id="role-form" onSubmit={handleSubmit(this.handleSubmit)} className="RoleFormContent">
        <Row>
          <Group>
            <TextInput
              name="name"
              placeholder={t('organisation:forms_role_name_placeholder')}
              maxLength={30}
              disabled={role && role.is_global}
            />
          </Group>
        </Row>
        <AsyncList
          // @ts-expect-error
          data={{
            onFetch: () => onFetchGroups(level),
            onPostFetch: this.handleInitialiseForm,
          }}
          renderRow={({ item }) => {
            const context = `${item.level}-${item.name}`;
            let selectedContext = '';

            if (item.options && selectedGroups[item.id]) {
              // Find the selected option by matching the full permissions array
              const selectedOption = item.options.find((option: any) => {
                return option.permissions.length === selectedGroups[item.id].length &&
                  option.permissions.every((perm: string) => selectedGroups[item.id].includes(perm));
              });

              if (selectedOption && selectedOption.sub_level) {
                selectedContext += `-${selectedOption.sub_level}`;
              }
            }

            return (
              <div className={[
                'PermissionGroup',
                item.is_selectable ? '' : 'PermissionGroup--unselectable',
              ].join(' ')}
              >
                <Checkbox
                  value={!!selectedGroups[item.id]}
                  disabled={!item.is_selectable || (role && role.is_global)}
                  size="large"
                  onChange={(value) => this.handleChange(item, value)}
                  label={(
                    <h6>
                      <Trans i18nKey={`organisation:forms_role_item_type_${context}`} />
                      {!item.is_selectable && <Icon type="shield" />}
                    </h6>
                  )}
                  description={(
                    <>
                      <Trans
                        i18nKey={`organisation:forms_role_item_type_${context}${selectedContext}-description`}
                        components={[<b>a</b>]}
                      />
                      {
                        i18n.exists(`organisation:forms_role_item_type_${context}-tooltip`) ?
                          (
                            <i>
                              <Trans i18nKey={`organisation:forms_role_item_type_${context}-tooltip`} />
                            </i>
                          ) :
                          null
                      }
                    </>
                  )}
                />
                {!!selectedGroups[item.id] && item.options && (() => {
                  const mappedOptions = item.options.map((option: any) => ({
                    ...option,
                    option_value: option.sub_level || 'all'
                  }));

                  // Find the selected option by matching the full permissions array
                  const selectedOption = mappedOptions.find((option: any) => {
                    return option.permissions.length === selectedGroups[item.id].length &&
                      option.permissions.every((perm: string) => selectedGroups[item.id].includes(perm));
                  });

                  return (
                    <Select
                      clearable={false}
                      value={selectedOption ? selectedOption.option_value : null}
                      valueKey="option_value"
                      options={mappedOptions}
                      optionRenderer={({ sub_level: subLevel }: any) => {
                        return <Trans
                          i18nKey={`organisation:forms_role_item_type_${context}-filter-${subLevel || 'all'}`}
                        />;
                      }}
                      valueRenderer={({ sub_level: subLevel }) => {
                        return (
                          <>{t(`organisation:forms_role_item_type_${context}-filter-${subLevel || 'all'}`)}</>
                        );
                      }}
                      onChange={(option) => this.handleChange({ id: item.id, permissions: option.permissions }, true)}
                    />
                  );
                })()}
              </div>
            );
          }}
        />
      </form>
    );
  }
}

export default withTranslation()(reduxForm({
  form: 'create-role',
  initialValues: {
    name: '',
  },
  validate: require('./validate').default,
// @ts-expect-error
})(RoleFormContent));
